import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCompetenciasComDado } from '../../api/queries/chamados'
import { usePainelDoIad } from '../../api/queries/iad'
import { useSessao } from '../../auth/contexto'
import { podeFiscalizar } from '../../auth/permissoes'
import { AvisoDeErro, Carregando, Esqueleto } from '../../components/Estados'
import { SemCompetencia } from '../../components/SemCompetencia'
import { TERMOS, TEXTOS } from '../../lib/glossario'
import { SeletorDeCompetencia } from '../chamados/SeletorDeCompetencia'
import { CandidatasDoItem16 } from './CandidatasDoItem16'
import { ConferenciaDoMes } from './ConferenciaDoMes'
import { FormularioDeOcorrencia } from './FormularioDeOcorrencia'
import { ListaDeOcorrencias } from './ListaDeOcorrencias'
import { PontuacaoDoMes } from './PontuacaoDoMes'

/**
 * Os pontos administrativos do mês.
 *
 * A competência vive na URL, como no fechamento: `/iad?competencia=2026-09` é um endereço que vai
 * por e-mail para quem vai conferir, e recarregar a página não pode devolver a pessoa a outro mês.
 *
 * **A ordem da tela é a ordem da decisão.** Primeiro quanto já custa (a pontuação e a faixa),
 * depois o que ainda é pergunta (as candidatas do item 16), depois o que já foi imputado, e só no
 * fim o formulário. Quem abre esta tela quase sempre vem responder "quanto isso vale este mês?" —
 * e não registrar algo novo.
 *
 * @returns a tela do indicador administrativo
 */
export function PaginaIad() {
  const [parametros, definirParametros] = useSearchParams()
  const { data: competencias, isPending: carregandoCompetencias } = useCompetenciasComDado()

  const escolhida = parametros.get('competencia') ?? ''
  const competencia = escolhida || competencias?.[0]?.competencia || ''

  const escolher = useCallback(
    (valor: string) => {
      const proximos = new URLSearchParams(parametros)
      proximos.set('competencia', valor)
      definirParametros(proximos)
    },
    [parametros, definirParametros],
  )

  return (
    <div className="space-y-5">
      <header>
        <h1 className="titulo-da-pagina">
          {TERMOS.iad.rotulo} ({TERMOS.iad.sigla})
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.iad.ajuda}</p>
      </header>

      {carregandoCompetencias && <Carregando />}

      {competencias && competencias.length > 0 && (
        <div className="cartao max-w-md p-4">
          <SeletorDeCompetencia competencias={competencias} valor={competencia} aoEscolher={escolher} />
        </div>
      )}

      {/* Sem nenhum mês com dado, o painel não era montado e a tela ficava com um cabeçalho e nada
          embaixo — a leitura de quem chega é "não carregou". É o estado em que a UAT começa. */}
      {competencias && competencias.length === 0 && <SemCompetencia />}

      {competencia && <Painel competencia={competencia} />}
    </div>
  )
}

/**
 * O painel de uma competência.
 *
 * @param props o mês a apurar
 * @returns a pontuação, as candidatas, as ocorrências e o formulário
 */
function Painel({ competencia }: { competencia: string }) {
  const { usuario } = useSessao()
  const { data: painel, isPending, isError, error } = usePainelDoIad(competencia)

  // Imputar ponto, conferir o mês, decidir candidata e retirar da soma são atos do fiscal
  // (`@PreAuthorize(Autorizacoes.FISCAL)` nos seis POSTs do `IadController`). O critério é lido uma
  // vez aqui e desce como propriedade: assim as quatro áreas abaixo decidem pelo MESMO critério, e
  // um teste que monte esta página com um LEITOR cobre as quatro de uma vez.
  const podeAgir = podeFiscalizar(usuario)

  if (isPending) {
    return <Esqueleto linhas={6} />
  }
  if (isError) {
    return <AvisoDeErro erro={error} />
  }

  return (
    <div className="space-y-5">
      <PontuacaoDoMes painel={painel} />
      <ConferenciaDoMes competencia={competencia} conferencia={painel.conferencia} podeAgir={podeAgir} />
      <CandidatasDoItem16 competencia={competencia} candidatas={painel.candidatas} podeAgir={podeAgir} />

      <section className="space-y-3" aria-label="Ocorrências do mês">
        <h2 className="text-base font-semibold text-slate-900">Ocorrências deste mês</h2>
        <ListaDeOcorrencias ocorrencias={painel.ocorrencias} podeAgir={podeAgir} />
      </section>

      {/* O formulário some inteiro para quem não imputa: ele não informa nada — é só o ato. As
          três áreas acima continuam à vista, porque elas são a apuração do mês. */}
      {podeAgir && <FormularioDeOcorrencia competencia={competencia} itens={painel.itens} />}
    </div>
  )
}
